import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, X } from "lucide-react";
import DealCard from "../DealCard";

/* V4.23 P0 — "Jetzt prüfen" results: total + market-signal breakdown +
 * the first REAL DealCards, plus "show all" that opens the map with the
 * EXACT same alert filters (no dead end). Data comes from
 * POST /api/saved-searches/{ulid}/run — same filter contract everywhere. */

const fmtInt = (n) => Number(n || 0).toLocaleString("de-AT");

function BreakdownChip({ label, value, tone, testid }) {
  const tones = {
    green: "border-emerald-500/40 bg-emerald-500/10 text-emerald-200",
    zinc: "border-zinc-700 bg-zinc-800/60 text-zinc-200",
    red: "border-red-500/40 bg-red-500/10 text-red-200",
    dim: "border-zinc-800 bg-zinc-900/60 text-zinc-500",
  };
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs ${tones[tone]}`}
    >
      <span className="font-semibold tabular-nums">{fmtInt(value)}</span>
      {label}
    </span>
  );
}

export default function AlertResultsSheet({ alert, result, mapHref, onClose }) {
  const { t } = useTranslation("common");
  if (!alert || !result) return null;
  const b = result.signal_breakdown || {};
  const cards = result.cards || [];
  const totalLabel = result.total_capped
    ? `${fmtInt(result.total)}+`
    : fmtInt(result.total);

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-black/60 backdrop-blur-sm"
      data-testid="alert-results-sheet"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-t-2xl border border-zinc-800 bg-zinc-950 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-zinc-800/70 px-5 py-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-amber-300/80">
              {t("alerts.resultsTitle")}
            </div>
            <div className="mt-0.5 text-base font-semibold text-zinc-100">
              {alert.name}
            </div>
            <p className="mt-1 text-sm text-zinc-400" data-testid="alert-results-summary">
              {t("alerts.resultsSummary", { total: totalLabel })}{" "}
              {b.under_market > 0 &&
                t("alerts.resultsSummaryDeals", { count: fmtInt(b.under_market) })}
            </p>
          </div>
          <button
            type="button"
            data-testid="alert-results-close"
            onClick={onClose}
            className="rounded-lg border border-zinc-800 p-1.5 text-zinc-400 hover:text-zinc-100"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex flex-wrap gap-2 px-5 py-3">
          <BreakdownChip tone="green" value={b.under_market}
            label={t("alerts.breakdownUnder")} testid="alert-results-under" />
          <BreakdownChip tone="zinc" value={b.fair}
            label={t("alerts.breakdownFair")} testid="alert-results-fair" />
          <BreakdownChip tone="red" value={b.over_market}
            label={t("alerts.breakdownOver")} testid="alert-results-over" />
          <BreakdownChip tone="dim" value={b.no_signal}
            label={t("alerts.breakdownNoSignal")} testid="alert-results-nosignal" />
        </div>

        <div className="flex-1 overflow-y-auto px-5 pb-4">
          {cards.length === 0 ? (
            <div
              data-testid="alert-results-empty"
              className="rounded-xl border border-dashed border-zinc-800 p-6 text-center text-sm text-zinc-500"
            >
              {t("alerts.resultsNone")}
            </div>
          ) : (
            <>
              <div className="mb-2 text-xs text-zinc-500">
                {t("alerts.resultsShowingBest", { count: cards.length })}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {cards.map((c) => (
                  <DealCard key={c.id} card={c} />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="border-t border-zinc-800/70 px-5 py-3">
          <Link
            to={mapHref}
            data-testid="alert-results-show-all"
            onClick={onClose}
            className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-2.5 text-sm font-medium text-amber-200 transition-colors hover:bg-amber-500/20"
          >
            <MapPin className="h-4 w-4" />
            {t("alerts.resultsShowAll", { total: totalLabel })}
          </Link>
        </div>
      </div>
    </div>
  );
}
